const ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

function randId(len = 6) {
  let out = '';
  for (let i = 0; i < len; i++) out += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)];
  return out;
}

export class ElenweaveGraph {
  constructor({ id = null, name = 'Untitled' } = {}) {
    this.id = id || `g_${randId(6)}`;
    this.name = name;
    this.nodes = [];
    this.nodeOrder = [];
    this.edges = [];
    this.notifications = [];
    this._edgeSeq = 1;
    this._edgeIdSet = new Set();
    this._idSet = new Set();
    this._notifIdSet = new Set();
  }

  generateId(prefix = 'n') {
    let id;
    do id = `${prefix}_${randId(6)}`;
    while (this._idSet.has(id));
    return id;
  }

  getNode(id) {
    return this.nodes.find((n) => n.id === id) || null;
  }

  _reserveId(id) {
    if (this._idSet.has(id)) return false;
    this._idSet.add(id);
    return true;
  }

  addNode(partial = {}) {
    const desired = partial.id ?? null;
    let id = desired || this.generateId();
    while (!this._reserveId(id)) {
      id = this.generateId();
    }

    const hasX = Number.isFinite(partial.x);
    const hasY = Number.isFinite(partial.y);
    const autoPlace = partial.autoPlace === true;
    const avoidOverlap = partial.avoidOverlap !== false;

    let x = hasX ? partial.x : 80;
    let y = hasY ? partial.y : 80;
    const w = Number.isFinite(partial.w) ? partial.w : 240;
    const h = Number.isFinite(partial.h) ? partial.h : 80;

    if (avoidOverlap && (autoPlace || !hasX || !hasY)) {
      const placed = this._findFreeSpot({ x, y, w, h });
      x = placed.x;
      y = placed.y;
    }

    const node = {
      id,
      type: partial.type || 'text',
      x,
      y,
      w,
      h,
      text: partial.text ?? (partial.type === 'html' ? '' : partial.type === 'html-text' ? 'Editable text' : 'New Node'),
      svg: partial.svg ?? null,
      svgUrl: partial.svgUrl ?? null,
      color: partial.color ?? null,
      pulse: Boolean(partial.pulse),
      data: partial.data ?? null,
      props: partial.props ?? null,
      component: partial.component ?? null,
      render: partial.render ?? null,
      loader: partial.loader ?? null,
      meta: partial.meta || null
    };

    if (partial.type === 'image') {
      node.img = partial.img || '';
      node.text = partial.text ?? '';
    }
    if (partial.type === 'audio') {
      node.audioUrl = partial.audioUrl || '';
      node.text = partial.text ?? 'Audio';
    }

    this.nodes.push(node);
    this.nodeOrder.push(node.id);
    return node.id;
  }

  addNodes(partials = []) {
    if (!Array.isArray(partials)) return [];
    const ids = [];
    partials.forEach((partial) => {
      if (!partial) return;
      const id = this.addNode(partial);
      if (id) ids.push(id);
    });
    return ids;
  }

  updateNode(id, patch = {}) {
    const node = this.getNode(id);
    if (!node) return null;
    Object.assign(node, patch);
    return node;
  }

  updateNodes(entries = []) {
    if (!Array.isArray(entries)) return [];
    const updated = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const id = entry.id ?? entry.nodeId ?? null;
      if (!id) return;
      const patch = entry.patch ?? entry.update ?? null;
      let payload = patch && typeof patch === 'object' ? patch : null;
      if (!payload) {
        const { id: _id, nodeId, patch: _patch, update: _update, ...rest } = entry;
        payload = rest;
      }
      const node = this.updateNode(id, payload);
      if (node) updated.push(node);
    });
    return updated;
  }

  removeNode(id) {
    const idx = this.nodes.findIndex((n) => n.id === id);
    if (idx < 0) return false;
    this.nodes.splice(idx, 1);
    this._idSet.delete(id);
    this.edges = this.edges.filter((e) => e.from !== id && e.to !== id);
    this.nodeOrder = this.nodeOrder.filter((nodeId) => nodeId !== id);
    return true;
  }

  addEdge(from, to, opts = {}) {
    if (!from || !to || from === to) return null;
    if (this.edges.some((e) => e.from === from && e.to === to)) return null;
    const desired = opts.id ?? null;
    let id = desired || this._generateEdgeId();
    while (!this._reserveEdgeId(id)) {
      id = this._generateEdgeId();
    }
    this.edges.push({
      id,
      from,
      to,
      color: opts.color ?? null,
      pulse: Boolean(opts.pulse),
      label: opts.label ?? null
    });
    return id;
  }

  addEdges(entries = []) {
    if (!Array.isArray(entries)) return [];
    const ids = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const from = entry.from ?? entry.source ?? entry.a ?? null;
      const to = entry.to ?? entry.target ?? entry.b ?? null;
      if (!from || !to || from === to) return;
      let opts = entry.opts ?? entry.options ?? null;
      if (!opts || opts === entry) {
        const { from: _from, to: _to, source, target, a, b, opts: _opts, options: _options, ...rest } = entry;
        opts = rest;
      }
      const id = this.addEdge(from, to, opts);
      if (id) ids.push(id);
    });
    return ids;
  }

  addNotification(partial = {}) {
    const nodeId = partial.nodeId;
    if (!nodeId) return null;
    const desired = partial.id ?? null;
    let id = desired || this._generateNotifId();
    if (!this._reserveNotifId(id)) {
      id = this._generateNotifId();
      this._reserveNotifId(id);
    }
    const notification = {
      id,
      nodeId,
      timestamp: Number.isFinite(partial.timestamp) ? partial.timestamp : Date.now(),
      read: Boolean(partial.read),
      label: partial.label ?? null
    };
    this.notifications.unshift(notification);
    if (this.notifications.length > 50) {
      const removed = this.notifications.pop();
      if (removed) this._notifIdSet.delete(removed.id);
    }
    this._sortNotifications();
    return id;
  }

  markNotificationRead(id, read = true) {
    const notif = this.notifications.find((n) => n.id === id);
    if (!notif) return null;
    notif.read = Boolean(read);
    return notif;
  }

  clearNotifications() {
    this.notifications = [];
    this._notifIdSet.clear();
  }

  updateEdge(edgeId, patch = {}) {
    const edge = this.edges.find((e) => e.id === edgeId);
    if (!edge) return null;
    Object.assign(edge, patch);
    return edge;
  }

  updateEdges(entries = []) {
    if (!Array.isArray(entries)) return [];
    const updated = [];
    entries.forEach((entry) => {
      if (!entry) return;
      const id = entry.id ?? entry.edgeId ?? null;
      if (!id) return;
      const patch = entry.patch ?? entry.update ?? null;
      let payload = patch && typeof patch === 'object' ? patch : null;
      if (!payload) {
        const { id: _id, edgeId, patch: _patch, update: _update, ...rest } = entry;
        payload = rest;
      }
      const edge = this.updateEdge(id, payload);
      if (edge) updated.push(edge);
    });
    return updated;
  }

  removeEdge(edgeId) {
    const idx = this.edges.findIndex((e) => e.id === edgeId);
    if (idx < 0) return false;
    const removed = this.edges.splice(idx, 1)[0];
    if (removed?.id) this._edgeIdSet.delete(removed.id);
    return true;
  }

  clear() {
    this.nodes = [];
    this.edges = [];
    this._idSet.clear();
    this._edgeSeq = 1;
    this.nodeOrder = [];
    this._edgeIdSet.clear();
    this.clearNotifications();
  }

  serialize() {
    return {
      id: this.id,
      name: this.name,
      nodeOrder: [...this.nodeOrder],
      nodes: this.nodes.map((n) => ({ ...n })),
      edges: this.edges.map((e) => ({ ...e })),
      notifications: this.notifications.map((n) => ({ ...n }))
    };
  }

  static hydrate(data) {
    const graph = new ElenweaveGraph({ id: data.id, name: data.name });
    if (Array.isArray(data.nodes)) {
      data.nodes.forEach((n) => {
        graph.addNode({ ...n, id: n.id });
      });
    }
    if (Array.isArray(data.edges)) {
      data.edges.forEach((e) => {
        const desired = typeof e.id === 'string' ? e.id : null;
        graph.addEdge(e.from, e.to, {
          id: desired,
          color: e.color ?? null,
          pulse: Boolean(e.pulse),
          label: e.label ?? null
        });
      });
      graph._syncEdgeSeqFromIds();
    }
    if (Array.isArray(data.notifications)) {
      const sorted = data.notifications
        .map((n) => ({
          id: n.id ?? null,
          nodeId: n.nodeId,
          timestamp: Number.isFinite(n.timestamp) ? n.timestamp : Date.now(),
          read: Boolean(n.read),
          label: n.label ?? null
        }))
        .filter((n) => n.nodeId)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 50);
      sorted.forEach((n) => {
        const desired = n.id || graph._generateNotifId();
        const id = graph._reserveNotifId(desired) ? desired : graph._generateNotifId();
        graph._notifIdSet.add(id);
        graph.notifications.push({ ...n, id });
      });
      graph._sortNotifications();
    }
    graph._applyNodeOrder(Array.isArray(data.nodeOrder) ? data.nodeOrder : null);
    return graph;
  }

  getNodeOrder() {
    return [...this.nodeOrder];
  }

  moveNodeToIndex(id, index) {
    if (!id) return false;
    const currentIndex = this.nodeOrder.indexOf(id);
    if (currentIndex < 0) return false;
    const nextIndex = Math.max(0, Math.min(index, this.nodeOrder.length - 1));
    if (currentIndex === nextIndex) return true;
    this.nodeOrder.splice(currentIndex, 1);
    this.nodeOrder.splice(nextIndex, 0, id);
    this._applyNodeOrder(this.nodeOrder);
    return true;
  }

  moveNodeBefore(id, targetId) {
    const targetIndex = this.nodeOrder.indexOf(targetId);
    if (targetIndex < 0) return false;
    return this.moveNodeToIndex(id, targetIndex);
  }

  moveNodeAfter(id, targetId) {
    const targetIndex = this.nodeOrder.indexOf(targetId);
    if (targetIndex < 0) return false;
    return this.moveNodeToIndex(id, targetIndex + 1);
  }

  _applyNodeOrder(order) {
    const ids = order ? order.filter((id) => this.nodes.find((n) => n.id === id)) : [];
    const missing = this.nodes.map((n) => n.id).filter((id) => !ids.includes(id));
    const nextOrder = [...ids, ...missing];
    this.nodeOrder = nextOrder;
    const map = new Map(this.nodes.map((n) => [n.id, n]));
    this.nodes = nextOrder.map((id) => map.get(id)).filter(Boolean);
  }

  _reserveEdgeId(id) {
    if (this._edgeIdSet.has(id)) return false;
    this._edgeIdSet.add(id);
    return true;
  }

  _generateEdgeId() {
    let id;
    do {
      id = `e_${this._edgeSeq++}`;
    } while (this._edgeIdSet.has(id));
    return id;
  }

  _syncEdgeSeqFromIds() {
    let max = 0;
    for (const edge of this.edges) {
      const match = /^e_(\d+)$/.exec(edge.id);
      if (match) max = Math.max(max, Number(match[1]));
      this._edgeIdSet.add(edge.id);
    }
    this._edgeSeq = Math.max(this._edgeSeq, max + 1);
  }

  _findFreeSpot(rect) {
    if (!this._overlaps(rect)) return rect;
    const step = Math.max(16, Math.round(Math.min(rect.w, rect.h) * 0.25));
    const maxRings = 18;
    for (let ring = 1; ring <= maxRings; ring++) {
      for (let dx = -ring; dx <= ring; dx++) {
        for (let dy = -ring; dy <= ring; dy++) {
          if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
          const candidate = {
            x: rect.x + dx * step,
            y: rect.y + dy * step,
            w: rect.w,
            h: rect.h
          };
          if (!this._overlaps(candidate)) return candidate;
        }
      }
    }
    return rect;
  }

  _overlaps(rect, padding = 12) {
    return this.nodes.some((node) => this._rectOverlap(rect, node, padding));
  }

  _rectOverlap(a, b, pad = 12) {
    const bx = b.x - pad;
    const by = b.y - pad;
    const bw = b.w + pad * 2;
    const bh = b.h + pad * 2;
    return a.x < bx + bw && a.x + a.w > bx && a.y < by + bh && a.y + a.h > by;
  }

  _sortNotifications() {
    this.notifications.sort((a, b) => b.timestamp - a.timestamp);
  }

  _reserveNotifId(id) {
    if (this._notifIdSet.has(id)) return false;
    this._notifIdSet.add(id);
    return true;
  }

  _generateNotifId() {
    let id;
    do id = `nt_${randId(6)}`;
    while (this._notifIdSet.has(id));
    return id;
  }
}

